import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api';
import { Empty, plural, fmtRub, fmtNum, fmtPct } from './charts';
import ExpenseForm, { todayIso } from './ExpenseForm';
import { nfConfirm } from '../../components/confirm';

// «Финансы → Затраты» (13.10): один месяц, группы по статьям, план и факт. Регулярные затраты
// дают план; факт — ✓ «Как в плане», своя сумма или «Подтвердить всё». «Закрыть месяц» — снимок:
// после него правки правил цифры месяца не меняют. Все запросы возвращают экран месяца целиком.

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const MONTHS_IN = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];
// «сентябрь», а из другого года — «декабрь 2025»
const monthName = (ym, relTo, table = MONTHS) => {
  const name = table[Number(ym.slice(5, 7)) - 1] || ym;
  return relTo && ym.slice(0, 4) !== relTo.slice(0, 4) ? `${name} ${ym.slice(0, 4)}` : name;
};
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');
const parseAmount = (v) => Number(String(v).replace(/\s/g, '').replace(',', '.'));
const CAN_REENTER = ['confirmed', 'manual'];
// Кнопки месяца не забирают фокус у поля факта: иначе blur-сохранение и действие летят наперегонки
const keepFocus = (e) => e.preventDefault();

/** Строка затраты: название (→ форма) + правило серым · план · факт */
function ExpenseRow({ row, disabled, onEdit, onFact, showToast, onGoMarketing }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const planStatus = row.status === 'plan';
  const inputMode = !disabled && (planStatus || (editing && CAN_REENTER.includes(row.status)));
  const startEdit = () => { setVal(row.fact != null ? String(Math.round(row.fact)) : ''); setEditing(true); };
  const save = async () => {
    const raw = val.trim();
    if (!raw) { setEditing(false); return; }
    const n = parseAmount(raw);
    if (!Number.isFinite(n) || n < 0) { showToast?.('Сумма — число', 'error'); return; }
    if (editing && row.fact != null && Math.abs(n - row.fact) < 0.005) { setEditing(false); return; }
    setBusy(true);
    try { await onFact(row, { fact: n }); setVal(''); setEditing(false); } catch (e) { /* тост показан */ } finally { setBusy(false); }
  };
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === 'Escape') { setVal(''); setEditing(false); }
  };
  const sub = [row.label, row.note].filter(Boolean).join(' · ');
  return (
    <div className="an-ex-row">
      <div className="an-ex-name-cell">
        {row.editable ? <button type="button" className="an-ex-name" onClick={() => onEdit(row)}>{row.title}</button> : <span className="an-ex-name-static">{row.title}</span>}
        {sub && <div className="an-muted an-small">{sub}</div>}
        {row.place && onGoMarketing && <button type="button" className="an-link an-small" onClick={onGoMarketing}>изменить в «Маркетинге» →</button>}
      </div>
      <div className="an-ex-plan"><span className="an-ex-cl an-muted an-small">план </span>{row.plan ? fmtRub(row.plan) : '—'}</div>
      <div className="an-ex-fact">
        <span className="an-ex-cl an-muted an-small">факт </span>
        {inputMode ? (<>
          <input className="form-input an-ex-input" inputMode="decimal" value={val} placeholder={row.plan ? fmtNum(row.plan) : '0'}
            aria-label={`Факт: ${row.title}`} onChange={(e) => setVal(e.target.value)} onKeyDown={onKey} onBlur={save} disabled={busy} autoFocus={editing} />
          {planStatus && <span className="an-muted an-small">план</span>}
          {planStatus && (
            <button type="button" className="an-ex-ok" title="Как в плане" aria-label={`Как в плане: ${row.title}`} disabled={busy}
              onMouseDown={(e) => e.preventDefault()} onClick={() => onFact(row, { as_plan: true }).catch(() => {})}>✓</button>
          )}
        </>) : (<>
          {!disabled && CAN_REENTER.includes(row.status)
            ? <button type="button" className="an-ex-num an-ex-num-btn" title="Изменить факт" onClick={startEdit}>{fmtRub(row.fact ?? row.plan)}</button>
            : <span className="an-ex-num">{fmtRub(row.fact ?? row.plan)}</span>}
          {row.status_text && <span className="an-muted an-small">{row.status_text}</span>}
          {!disabled && CAN_REENTER.includes(row.status) && (
            <button type="button" className="an-link" onClick={() => onFact(row, { fact: null }).catch(() => {})}>Вернуть план</button>
          )}
        </>)}
      </div>
    </div>
  );
}

// «Перенести из прошлого месяца» (25.10): статьи справочника без «Прочего» и продвижения —
// галочки и суммы (факт прошлого месяца, нет факта — план); переносятся планом на этот месяц
function CarryModal({ carry, month, onDone, onClose }) {
  const [rows, setRows] = useState(() => carry.items.map(x => ({ ...x, on: true, value: String(x.amount) })));
  const [busy, setBusy] = useState(false);
  const picked = rows.filter(r => r.on && parseAmount(r.value) > 0);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onDone(picked.map(r => ({ expense_id: r.expense_id, amount: parseAmount(r.value) })));
      onClose();
    } catch (err) { /* тост уже показан */ } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2 className="modal-title">Перенести затраты из {carry.from_title} в {monthName(month, null, MONTHS)}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button></div>
        <div className="modal-body an-stack" style={{ gap: 8 }}>
          {rows.map((r, i) => (
            <label key={r.expense_id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={r.on} onChange={(e) => setRows(rs => rs.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} />
              <span style={{ flex: 1, minWidth: 0 }}>{r.title}{r.title !== r.category_title && <span className="an-muted an-small"> · {r.category_title}</span>}</span>
              <input className="form-input" style={{ width: 110, textAlign: 'right' }} inputMode="decimal" value={r.value} disabled={!r.on}
                aria-label={`Сумма: ${r.title}`} onChange={(e) => setRows(rs => rs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <span className="an-muted">₽</span>
            </label>
          ))}
          <div className="an-muted an-small">«Прочее» и продвижение не переносим. В {monthName(month, null, MONTHS_IN)} затраты встанут планом — факт подтвердите в конце месяца, как обычно.</div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Отмена</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !picked.length}>
            {busy ? 'Переношу…' : `Перенести ${plural(picked.length, 'затрату', 'затраты', 'затрат')}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ExpensesMonth({ showToast, onGoMarketing }) {
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);   // {item?, category?}
  const [carry, setCarry] = useState(null);       // что можно перенести из прошлого месяца
  const [carryOpen, setCarryOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getExpenseMonth(month).then(r => { if (alive) setData(r); }).catch(e => showToast?.(e.message, 'error')).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, showToast]);
  // после переноса или добавления затраты список кандидатов меняется — перечитываем
  const rowsKey = (data?.groups || []).map(g => `${g.category}:${g.rows.length}`).join(',');
  useEffect(() => {
    let alive = true;
    setCarry(null);
    api.getExpenseCarry(month).then(r => { if (alive) setCarry(r); }).catch(() => {});
    return () => { alive = false; };
  }, [month, rowsKey]);
  const run = useCallback(async (fn, ok) => {
    try {
      const res = await fn();
      setData(res);
      if (ok) showToast?.(ok);
      return res;
    } catch (e) {
      showToast?.(e.message, 'error');
      throw e;
    }
  }, [showToast]);
  const setFact = (row, body) => run(() => api.setExpenseFact(row.expense_id, month, body), 'Сохранено');
  const saveExpense = (item, values) => run(() => (item ? api.updateExpense(item.expense_id, month, values) : api.createExpense(month, values)), 'Сохранено');
  const deleteExpense = (item) => run(() => api.deleteExpense(item.expense_id, month), 'Удалено');
  const confirmAll = () => { const n = data.unconfirmed; run(() => api.confirmExpenseMonth(month), `Подтверждено: ${n}`).catch(() => {}); };
  const close = async () => {
    if (data.unconfirmed > 0 && !(await nfConfirm(`У ${plural(data.unconfirmed, 'затраты', 'затрат', 'затрат')} факт не подтверждён — закроем их как в плане. Закрыть ${monthName(month)}?`, { confirmText: 'Закрыть месяц' }))) return;
    run(() => api.closeExpenseMonth(month), 'Месяц закрыт').catch(() => {});
  };
  const reopen = () => run(() => api.reopenExpenseMonth(month), 'Месяц открыт заново').catch(() => {});
  const doCarry = (items) => run(() => api.carryExpenses(month, items), `Перенесено: ${items.length}`);

  if (!data) return <div className="an-muted" style={{ padding: 24 }}>Загрузка…</div>;
  const closed = data.state === 'closed';
  const groups = data.groups || [];
  const t = data.totals || {};
  const unclosed = (data.unclosed || []).map(m => monthName(m, month));
  const addBtn = (category) => <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm({ category })}>+ Добавить затрату</button>;
  const canCarry = !closed && carry?.items?.length > 0;
  const carryBtn = (primary) => canCarry && (
    <button type="button" className={`btn ${primary ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setCarryOpen(true)}>
      Перенести из {carry.from_title} ({carry.items.length})
    </button>
  );
  return (
    <div className="an-stack" style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <div className="an-ex-head">
        <div className="an-ex-nav">
          <button type="button" className="btn-icon" aria-label="Предыдущий месяц" title="Предыдущий месяц" disabled={!data.prev} onClick={() => data.prev && setMonth(data.prev)}>‹</button>
          <h2 className="an-ex-title">{data.title}</h2>
          <button type="button" className="btn-icon" aria-label="Следующий месяц" title="Следующий месяц" disabled={!data.next} onClick={() => data.next && setMonth(data.next)}>›</button>
        </div>
        <div className="an-ex-actions">
          {groups.length > 0 && carryBtn(false)}
          {data.unconfirmed > 0 && !closed && <button type="button" className="btn btn-secondary btn-sm" onMouseDown={keepFocus} onClick={confirmAll}>Подтвердить всё как в плане</button>}
          {data.state === 'open' && <button type="button" className="btn btn-primary btn-sm" onMouseDown={keepFocus} onClick={close}>Закрыть месяц</button>}
          {data.state === 'current' && data.close_hint && <span className="an-muted an-small">{data.close_hint}</span>}
          {closed && <span className="an-muted an-small" title="Затраты месяца зафиксированы. Заказы, списания и закупки этого месяца по-прежнему меняются — оборот и прибыль за месяц пересчитаются">закрыт {fmtD(data.closed_at)}</span>}
          {closed && <button type="button" className="an-link" onClick={reopen}>Открыть заново</button>}
          {!closed && data.reopened_at && <span className="an-muted an-small">открыт заново {fmtD(data.reopened_at)}</span>}
        </div>
      </div>

      {!data.any ? (
        <div className="card an-panel" style={{ '--ch': '12px' }}>
          <div className="an-panel-body">
            <Empty>Затрат пока нет. Внесите аренду, зарплату и другие постоянные расходы — в «Финансах» появится операционная прибыль и точка безубыточности.</Empty>
            {addBtn()}
          </div>
        </div>
      ) : !groups.length ? (
        <div className="card an-panel" style={{ '--ch': '12px' }}>
          <div className="an-panel-body">
            <Empty>В {monthName(month, null, MONTHS_IN)} затрат нет</Empty>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{carryBtn(true)}{addBtn()}</div>
          </div>
        </div>
      ) : (<>
        <div className="an-ex-sub">
          Затраты месяца <b>{fmtRub(t.total)}</b>
          {t.fact > 0 && <> · подтверждено {fmtRub(t.fact)}</>}
          {t.plan > 0 && <> · по плану {fmtRub(t.plan)}</>}
          {unclosed.length > 0 && <span className="an-muted"> · не закрыты: {unclosed.join(', ')}</span>}
        </div>
        {closed && (
          <div className="an-muted an-small an-ex-sub">Затраты месяца зафиксированы. Заказы, списания и закупки закрытие не трогает — поправили заказ, оборот и прибыль месяца пересчитаются.</div>
        )}
        {data.breakeven && (
          <div className="an-muted an-small an-ex-sub">
            Чтобы покрыть затраты месяца ({fmtRub(data.breakeven.costs)}), при марже {fmtPct(data.breakeven.margin_pct, 0)} нужен оборот около {fmtRub(data.breakeven.turnover)}.
          </div>
        )}
        <section className="card an-ex-list" style={{ '--ch': '12px' }}>
          {groups.map(g => (
            <div key={g.category} className="an-ex-group">
              <div className="an-ex-row an-ex-group-head">
                <div><b>{g.title}</b> <span className="an-ex-sum">{fmtRub(g.total)}</span>
                  {g.plan > 0 && <span className="an-muted an-small"> · по плану {fmtRub(g.plan)}</span>}</div>
                <div className="an-ex-plan an-ex-col an-muted an-small">План</div>
                <div className="an-ex-fact an-ex-col an-muted an-small">Факт</div>
              </div>
              {(g.category === 'marketing' && data.targets
                // Продвижение: общий маркетинг (вносится здесь) и расходы по местам (из «Маркетинга») — 14.10
                ? [['Общий маркетинг', g.rows.filter(r => !r.place)], ['По местам — из «Маркетинга»', g.rows.filter(r => r.place)]]
                : [[null, g.rows]]
              ).map(([sub, rows]) => (sub && !rows.length ? null : (
                <React.Fragment key={sub || 'all'}>
                  {sub && <div className="an-ex-subhead an-muted an-small">{sub}</div>}
                  {rows.map(row => (
                    <ExpenseRow key={row.expense_id ?? `${g.category}-${row.title}`} row={row} disabled={closed} showToast={showToast}
                      onGoMarketing={onGoMarketing}
                      onEdit={(r) => setForm({ item: { ...r, title: r.item_title || '' }, category: g.category })} onFact={setFact} />
                  ))}
                </React.Fragment>
              )))}
              <div className="an-ex-foot">
                <button type="button" className="an-link" onClick={() => setForm({ category: g.category })}>{g.category === 'marketing' && data.targets ? '+ Общий маркетинг' : '+ Добавить'}</button>
                {g.category === 'marketing' && data.targets && onGoMarketing && (
                  <button type="button" className="an-link" onClick={onGoMarketing}>Расход у места — в «Маркетинг → Ссылки и QR» →</button>
                )}
              </div>
            </div>
          ))}
        </section>
        <div>{addBtn()}</div>
      </>)}

      <div className="an-text an-muted an-ex-how">
        <p>Как считаем: регулярные затраты дают план на месяц; подтвердите факт галочкой или впишите свою сумму. Пока факта нет, в отчётах считается план. «Закрыть месяц» фиксирует затраты месяца — после этого их правки не меняют цифры месяца. Заказы, списания и закупки закрытие не трогает: поправили заказ — оборот и прибыль месяца пересчитаются.</p>
      </div>

      {carryOpen && canCarry && <CarryModal carry={carry} month={month} onDone={doCarry} onClose={() => setCarryOpen(false)} />}

      {form && (
        <ExpenseForm mode="all" item={form.item} category={form.category} categories={data.categories} targets={data.targets} month={month}
          onSave={saveExpense} onDelete={deleteExpense} onClose={() => setForm(null)} />
      )}
    </div>
  );
}
